import React, { useCallback, useMemo, useState } from "react";
import { Button, Checkbox, Flex, Text } from "@jsq/ds/core";
import { GP_CASE_TYPE_LABELS } from "../constants";
import { FilterPopover } from "./FilterPopover";
import { checkboxItem, checkboxList, filterPanelFooter, dateInput } from "./styles_vanilla.css";
import type { ComplianceCaseTypeEnum } from "../../../../graphql/supergraphTypes";

type Props = {
  value: ComplianceCaseTypeEnum[] | null;
  onChange: (caseTypes: ComplianceCaseTypeEnum[] | null) => void;
};

const ALL_CASE_TYPES = Object.keys(GP_CASE_TYPE_LABELS) as ComplianceCaseTypeEnum[];

const buildTriggerLabel = (selected: ComplianceCaseTypeEnum[] | null): string => {
  if (!selected?.length) return "Activity type";
  if (selected.length === 1) return GP_CASE_TYPE_LABELS[selected[0]] ?? "Activity type";
  return `${selected.length} types`;
};

export const ActivityTypeFilter: React.FC<Props> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [localSelected, setLocalSelected] = useState<ComplianceCaseTypeEnum[]>([]);

  const filteredTypes = useMemo(() => {
    const q = search.toLowerCase();
    return ALL_CASE_TYPES.filter((t) =>
      (GP_CASE_TYPE_LABELS[t] ?? "").toLowerCase().includes(q),
    );
  }, [search]);

  const isAllSelected = localSelected.length === ALL_CASE_TYPES.length;

  const toggleSelectAll = useCallback(() => {
    setLocalSelected(isAllSelected ? [] : [...ALL_CASE_TYPES]);
  }, [isAllSelected]);

  const toggleType = useCallback((caseType: ComplianceCaseTypeEnum) => {
    setLocalSelected((prev) =>
      prev.includes(caseType) ? prev.filter((t) => t !== caseType) : [...prev, caseType],
    );
  }, []);

  const handleApply = useCallback(() => {
    onChange(isAllSelected ? null : localSelected);
    setIsOpen(false);
  }, [isAllSelected, localSelected, onChange]);

  const handleOpen = useCallback(() => {
    setLocalSelected(value ?? [...ALL_CASE_TYPES]);
    setSearch("");
    setIsOpen((o) => !o);
  }, [value]);

  return (
    <FilterPopover
      label={buildTriggerLabel(value)}
      isActive={value != null}
      isOpen={isOpen}
      onToggle={handleOpen}
      onClose={() => setIsOpen(false)}
    >
      <input
        type="text"
        placeholder="Search activity type"
        className={dateInput}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className={checkboxList}>
        <Checkbox
          label="(Select all)"
          checked={isAllSelected}
          indeterminate={localSelected.length > 0 && !isAllSelected}
          onChange={toggleSelectAll}
          wrapperClassName={checkboxItem}
        />
        {filteredTypes.map((caseType) => (
          <Checkbox
            key={caseType}
            label={GP_CASE_TYPE_LABELS[caseType] ?? caseType}
            checked={localSelected.includes(caseType)}
            onChange={() => toggleType(caseType)}
            wrapperClassName={checkboxItem}
          />
        ))}
        {filteredTypes.length === 0 && <Text variant="regular-sm">No matching types</Text>}
      </div>

      <Flex justifyContent="flex-end" alignItems="center" className={filterPanelFooter}>
        <Button variant="primary" onClick={handleApply}>
          Apply
        </Button>
      </Flex>
    </FilterPopover>
  );
};
